import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { cn } from '@/lib/cn'
import { HOLDINGS } from './crypto-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

const SPREAD = 0.001
const MINIMUM = 1000

export function CryptoConvertScreen() {
  const navigate = useNavigate()
  const [symbol, setSymbol] = useState('USDC')
  const [amount, setAmount] = useState('1,000')
  const asset = HOLDINGS.find((item) => item.symbol === symbol) ?? HOLDINGS[0]!

  const model = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const units = Number.isFinite(parsed) && parsed > 0 ? parsed : 0
    const gross = fromMajor(units * asset.priceUsd, 'USD')
    const fee = money(Math.round(gross.minorUnits * SPREAD), 'USD')
    const net = money(gross.minorUnits - fee.minorUnits, 'USD')
    return { units, gross, fee, net, tooSmall: gross.minorUnits > 0 && gross.minorUnits < MINIMUM }
  }, [amount, asset])

  return (
    <>
      <PageHeader
        title="Convert to spendable"
        subtitle="Move crypto into your US Dollar balance. It settles instantly and you can spend it with the card."
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to="/crypto">
            Back to wallet
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  Convert from
                </Text>
                <div className={styles.corridors}>
                  {HOLDINGS.map((item) => (
                    <button
                      key={item.symbol}
                      type="button"
                      className={cn(styles.corridor, item.symbol === symbol && styles.corridorActive)}
                      onClick={() => {
                        setSymbol(item.symbol)
                        setAmount(item.symbol === 'USDC' ? '1,000' : item.amount)
                      }}
                      aria-pressed={item.symbol === symbol}
                    >
                      <Stack gap={0} align="start">
                        <Text variant="caption" weight="semibold">
                          {item.symbol}
                        </Text>
                        <Text variant="eyebrow" tone="secondary" numeric>
                          {item.amount} available
                        </Text>
                      </Stack>
                    </button>
                  ))}
                </div>
              </Stack>

              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  How much {asset.symbol}?
                </Text>
                <div className={styles.amountField}>
                  <Text variant="eyebrow" tone="secondary">
                    {asset.amount} {asset.symbol} in your wallet
                  </Text>
                  <div className={styles.amountRow}>
                    <input
                      className={styles.amountInput}
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                      aria-label={`Amount of ${asset.symbol} to convert`}
                    />
                    <Text variant="subtitle" weight="semibold" tone="secondary">
                      {asset.symbol}
                    </Text>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setAmount(asset.amount)}>
                  Convert everything
                </Button>
                {model.tooSmall && (
                  <Stack direction="row" align="center" gap={2}>
                    <Icon name="error" size={16} />
                    <Text variant="eyebrow" tone="negative" weight="semibold">
                      The minimum conversion is $10.00.
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                Into your US Dollar account
              </Text>
              <Pill tone="accent">Instant</Pill>
            </Card.Header>
            <Stack direction="row" align="center" gap={3} className="pb-mt-4">
              <CurrencyBadge currency="USD" size={40} />
              <Text as="p" variant="display" weight="bold" tone="accent" numeric>
                {formatMoney(model.net)}
              </Text>
            </Stack>

            <div className={checkout.rule} />

            {[
              [`${asset.symbol} converted`, `${model.units} ${asset.symbol}`],
              ['At', `$${asset.priceUsd.toLocaleString('en-US')}`],
              ['Gross', formatMoney(model.gross)],
              ['Spread · 0.10%', formatMoney(model.fee)],
              ['Conversion fee', 'None'],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 4 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}

            <Card.Footer>
              <Button
                size="lg"
                block
                iconEnd="arrow_forward"
                disabled={model.gross.minorUnits === 0 || model.tooSmall}
                onClick={() => navigate('/crypto/convert/done', { replace: true })}
              >
                Convert {formatMoney(model.net)}
              </Button>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
